import React from 'react'
import { motion } from 'framer-motion'
import { EmptyProps } from 'lib/types'
import { Paper } from 'components/Paper'
import { useRouter } from 'next/router'
import Image from 'next/image'

const Index: React.FC<EmptyProps> = () => {
  const router = useRouter()

  return (
    <div>
      <div className='flex flex-row px-10 py-4 items-center space-x-2'>
        <div className='pt-2 text-3xl'>
          <Image src='/supercharge_logo.svg' width={40} height={40} />
        </div>

        <div className='text-2xl font-bold text-gray-900 z-10'>
          Super<span className='text-indigo-700'>charge</span>
        </div>
      </div>
      <div className='relative z-20'>
        <header className='py-10'>
          <div className='max-w-7xl mx-auto px-24 space-y-6'>
            <motion.div
              animate={{ opacity: [0, 1] }}
              transition={{ duration: 1 }}
            >
              <button
                type='button'
                onClick={() => router.push('/')}
                className='inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              >
                Back
              </button>
              <div className='relative px-4 sm:px-6 lg:px-8'>
                <div className='text-lg max-w-prose mx-auto'>
                  <h1>
                    <span className='block text-base text-center text-indigo-600 font-semibold tracking-wide uppercase'>
                      SUPERCHARGE
                    </span>
                    <span className='mt-2 block text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
                      Privacy Policy
                    </span>
                  </h1>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Last updated July 20, 2022</strong>
                    </p>
                  </div>
                </div>
                <div className='relative px-4 sm:px-6 lg:px-8'>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge Software, Inc. and its affiliates
                    ("Supercharge," “we” or “our”), know that you care about how
                    your personal information is used and shared, and we take
                    your privacy seriously. Our primary goal is to provide you
                    with exceptional service, and we understand that you may
                    have questions or concerns regarding your personal
                    information and how it will be used. To make this privacy
                    policy easy to find, we make it available from home page of
                    our website at www.supercharge.finance (the "website") and
                    through our applications (together with the website, the
                    “Services”). You may also email us with any privacy-related
                    questions you have to hello@supercharge.finance. We may
                    periodically post changes to this Privacy Policy on this
                    page, and it is your responsibility to review this Privacy
                    Policy frequently and we encourage you to visit this page
                    often. When required by law, we will notify you of any
                    changes to this Privacy Policy. By using or accessing the
                    Services in any manner, you acknowledge that you accept the
                    practices and policies outlined in this Privacy Policy, and
                    you hereby consent that we will collect, use and share your
                    personal information as set forth below.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Applicability of Privacy Policy</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    This privacy policy applies to all personal information we
                    collect through our web applications. This policy does not
                    apply to the practices of companies that Supercharge does
                    not own or control, or to individuals that Supercharge does
                    not employ or manage.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Throughout this policy, we use the term "personal
                    information" to describe information that can be associated
                    with a specific person or company and can be used to
                    identify that person, including Account Information,
                    Identification Information, Financial Information and KYC
                    Information, each as described below. We do not consider
                    personal information to include information that has been
                    anonymized so that it does not identify a specific user.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>The Information We Collect</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    When you visit the website or use one of our applications,
                    we collect your IP address, and standard web log
                    information, such as your browser type and the pages you
                    accessed on our website. We also may collect certain
                    geo-location information. If you do not agree to our
                    collection of this information, you may not be able to use
                    our Service.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        We May Also Collect the Below Information From You.
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        - “Account Information” meaning text-enabled
                        cellular/wireless telephone number, machine or mobile
                        device ID.
                      </li>
                      <li>
                        {' '}
                        - “Identification Information” meaning your name, street
                        address, email address, date of birth, and SSN (or other
                        governmental issued verification numbers).
                      </li>
                      <li>
                        {' '}
                        - "Device Information" – We also may obtain information
                        about you: (a) from your mobile device such as your
                        device type, machine or mobile device identification
                        number, geo-location, time zone, language setting,
                        browser type, and IP address, and (b) from third parties
                        for purposes of transaction processing, identity
                        verification, fraud detection or prevention and other
                        similar purposes. For fraud prevention purposes, we also
                        may link your machine ID with the machines of others who
                        use your same payment cards.
                      </li>
                      <li>
                        {' '}
                        - “Financial Information” meaning bank account and
                        routing numbers and credit cards linked to your
                        Supercharge account.
                      </li>
                      <li>
                        {' '}
                        - Know Your Customer Information (“KYC Information”)
                        meaning information collected through a W-9 and other
                        Identification Information.
                      </li>
                    </ul>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We are committed to providing safe, secure and all-around
                    great Services. Therefore, before permitting you to use our
                    Services, we may require additional information from you we
                    can use to verify your identity, address or other
                    information or to manage risk and compliance throughout our
                    relationship. We may also obtain personal information about
                    you from third parties such as identity verification, fraud
                    prevention and similar services.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    When you are using our Services, we collect information
                    about your account transactions and trust relationships and
                    we may collect information about your computer or other
                    access device for fraud prevention and other similar
                    purposes. We may also collect personal information related
                    to your contact with our customer support team or store
                    results when you respond to a survey.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We do not seek or knowingly collect any personal information
                    about children under 13 years of age. If we become aware
                    that we have unknowingly collected information about a child
                    under 13 years of age, we will make commercially reasonable
                    efforts to delete such information from our database. If you
                    are the parent or guardian of a child under 13 years of age
                    who has provided us with their personal information, you may
                    contact us using the below information to request that it be
                    deleted.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        How We Use Cookies and Other Tracking Technologies
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    When you access our website, we, or companies we hire to
                    track how our website is used, may place small data files
                    called cookies or pixel tags on your computer or other
                    device. For example, we send a "session cookie" to your
                    computer any time you log in to your Supercharge account.
                    This type of cookie helps us to recognize you if you visit
                    multiple pages on our site during the same session, so that
                    we don't need to ask you for your password on each page. We
                    use these technologies to recognize you as a Supercharge
                    customer, customize content and advertising, measure
                    promotional effectiveness, and collect information about
                    your computer or other access device to mitigate risk, help
                    prevent fraud, and promote trust and safety. The use of
                    these cookies are optional.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge uses cookies to deliver content and
                    advertisements specific to your interests on our sites and
                    elsewhere on the Internet. Supercharge uses some third
                    parties to show you Supercharge-related advertisements that
                    are tailored to you based upon your interests or online
                    behaviors.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Users are free to decline our cookies if your browser
                    permits, but doing so may interfere with your use of our
                    website.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        How We Protect and Store Personal Information
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We store and process your personal information using
                    third-party servers located in secure data centers in the
                    United States. This information is protected by physical,
                    electronic and procedural safeguards in compliance with
                    applicable U.S. federal and state regulations. We also use
                    computer safeguards such as firewalls and data encryption,
                    we enforce physical access controls to our office and files,
                    and we authorize access to personal information only for
                    those employees who require it to fulfill their job
                    responsibilities. By using Supercharge, you are consenting
                    to have your personal information stored, transferred and
                    processed in the United States.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We make reasonable efforts to ensure security on our
                    systems. Despite our efforts, we cannot guarantee that
                    personal information may not be accessed, disclosed, altered
                    or destroyed by breach of our administrative, managerial and
                    technical safeguards. Therefore, we urge you to take
                    adequate precautions to protect your personal information as
                    well, including never sharing your password with anyone.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    If Supercharge learns of a systems security breach, we may
                    attempt to notify you electronically so that you can take
                    appropriate protective steps. By using our services, you
                    agree that Supercharge may communicate with you
                    electronically. Supercharge may post a notice on the Website
                    if a security breach occurs. Supercharge may also send an
                    email to you at the email address you have provided to us.
                    Depending on where you live, you may have a legal right to
                    receive notice of a security breach in writing. To receive
                    free written notice of a security breach (or to withdraw
                    your consent from receiving electronic notice of a security
                    breach) you should notify us.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        How We Use the Personal Information We Collect
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Our primary purpose in collecting personal information is to
                    provide you with a safe, smooth, efficient, fun and
                    customized experience.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may use your personal information to:
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        - provide the services and customer support you request;
                      </li>
                      <li>
                        {' '}
                        - process transactions and send notices about your
                        transactions or your network activity;
                      </li>
                      <li>
                        {' '}
                        - resolve disputes, collect fees, and troubleshoot
                        problems;
                      </li>
                      <li>
                        {' '}
                        - prevent potentially fraudulent, prohibited or illegal
                        activities, and enforce our User Agreement;
                      </li>
                      <li>
                        {' '}
                        - compare information for accuracy and verify it with
                        third parties; and
                      </li>
                      <li> - perform other duties as required by law.</li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Retention Policy</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We only store personal information as long as it is needed
                    for the purpose for which it was collected. We will keep
                    Account Information only as long as you are registered as a
                    user. However, after you delete your account, we may process
                    such personal information as is necessary for us to fulfill
                    an agreement with you, or such personal information as we
                    are required to keep by law.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    In order for us to provide our Service and for the purposes
                    described in this policy, we will keep any Financial
                    Information for 5 years.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    All personal information processed in connection with
                    cookies are stored in accordance with our cookie policy as
                    described below.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        How we Share Personal Information with other Supercharge
                        Users{' '}
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    To process your payments on Supercharge, we need to share
                    some of your personal information with the person or company
                    that you are paying or is paying you. Your contact
                    information, date of sign-up, the number of payments you
                    have received and other verification metrics like social
                    graph activity may be provided to users who you transact
                    with on or through Supercharge. Regardless, we will not
                    disclose your credit card number or bank account number to
                    anyone you have paid or who has paid you through
                    Supercharge, except with your express permission or if we
                    are required to do so to comply with a subpoena or other
                    legal process.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        How We Share Personal Information with Other Parties
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge does not share your email address or phone
                    number with third parties for promotional or marketing
                    purposes.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may share your personal information with:
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        - Companies that we plan to merge with or be acquired by
                        or, in the event of any bankruptcy, a bankruptcy estate.
                        Should such a combination occur, we will require that
                        the new combined entity follow this privacy policy with
                        respect to your personal information. If your personal
                        information could be used contrary to this policy, you
                        will receive prior notice and the opportunity to
                        communicate preferences you may have, if applicable.{' '}
                      </li>
                      <li> - Financial institutions with which we partner;</li>
                      <li>
                        {' '}
                        - Law enforcement, government officials, or other third
                        parties when we are compelled to do so by a subpoena,
                        court order or similar legal procedure, when we need to
                        do so to comply with law we believe in good faith that
                        the disclosure of personal information is necessary to
                        prevent physical harm or financial loss, to report
                        suspected illegal activity, or to investigate violations
                        of our User Agreement, or as otherwise required by law;
                      </li>
                      <li>
                        {' '}
                        - Third party service providers who assist us in
                        providing services to you or who provide fraud detection
                        or similar services on our behalf;
                      </li>
                      <li>
                        {' '}
                        - Service providers under contract who help with parts
                        of our business operations such as marketing and
                        technology services. Our contracts require these service
                        providers to only use your information in connection
                        with the services they perform for us, and prohibit them
                        from selling your information to anyone else;
                      </li>
                      <li>
                        {' '}
                        - Other third parties with your consent or direction to
                        do so;
                      </li>
                      <li>
                        {' '}
                        - Supercharge may use Plaid Technologies, Inc. to verify
                        your bank account. Supercharge only shares your
                        information with Plaid in accordance with this Privacy
                        Policy. Information shared with Plaid is treated by
                        Plaid in accordance with its Privacy Policy, available
                        at https://plaid.com/legal/.
                      </li>
                      <li>
                        {' '}
                        - Supercharge may use Prime Trust, LLC. to perform
                        banking services, Know Your Customer, anti-money
                        laundering & fraud checks. Supercharge only shares your
                        information with Prime Trust in accordance with this
                        Privacy Policy. Information shared with Prime Trust is
                        treated by Prime Trust in accordance with its Privacy
                        Policy, available at
                        https://www.primetrust.com/legal/updated-privacy-policy.
                      </li>
                    </ul>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may also use third-party KYC and anti-money laundering
                    tools to verify the identity of users to help combat fraud
                    and improve user experiences.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    If you establish a Supercharge account indirectly on a third
                    party website or via a third party application, any
                    information that you enter on that website or application
                    (and not directly on Supercharge's website) will be shared
                    with the owner of the third party website or application and
                    your information may be subject to their privacy policies.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        Your Rights and Choices as it relates to your Personal
                        Information
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You have the right to ask us not to process your personal
                    information where we process it on the basis of your
                    legitimate interests. You also can review and update your
                    personal information at any time by logging into your
                    supercharge.finance account. You should promptly update your
                    personal information if it changes or is inaccurate.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You can delete your account by emailing
                    hello@supercharge.finance. When you request for us to delete
                    your account for the Service, your personal information will
                    be permanently deleted from our servers and access to your
                    account will be disabled. We may retain your personal
                    information in accordance with our retention policy and to
                    the extent required to comply with (or we deem it reasonable
                    in light of) any laws or regulations.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You have the right to receive confirmation on whether or not
                    we process personal information concerning you, and in such
                    cases get access to such personal information and also
                    information regarding the personal information and how we
                    process it.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You can request a copy of your personal information which we
                    hold, relating to you and that you have provided us, in a
                    commonly used electronic format (in Europe this is known as
                    a subject access request). If you would like a copy of some
                    or all of it, please contact us and let us know the
                    information you want a copy of, including any account or
                    reference numbers, if you have them. Any subject access
                    request may be subject to a reasonable fee to cover the cost
                    of providing you with details of the information we hold
                    about you.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You can require us to correct any mistakes in your
                    information which we hold without charge or undue delay.
                    Taking into account the purposes of the processing, you also
                    have the right to have incomplete personal information about
                    you completed. If you would like to do this, please contact
                    us and let us know the information that is incorrect and the
                    information you want it replaced with.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You have the right to withdraw a consent provided by you at
                    any time by contacting us. If your consent is withdrawn, we
                    will no longer process your personal information for the
                    purpose that you had given your consent to. In some
                    circumstances, you have the right to obtain restriction of
                    the processing of your personal information. For example, if
                    you contest the accuracy of the personal information, you
                    can also require that we restrict the processing of your
                    personal information for such a period that enables us to
                    verify the accuracy of the personal information. You have
                    the right to object to processing of your personal
                    information that is based on our legitimate interests. If
                    this is done, we must provide compelling legitimate grounds
                    for the processing which overrides your interests, rights
                    and freedoms, in order to proceed with the processing of
                    your personal information.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You may always contact Supercharge at
                    hello@supercharge.finance if you have any questions or
                    comments regarding our processing of your personal
                    information. You also have the right to complain about the
                    processing of your personal information by lodging a
                    complaint to the relevant data protection authority.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>
                        California Residents - Your California Privacy Rights:
                      </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    California's Shine the Light Law: We do not disclose your
                    personal information to third parties for the purpose of
                    directly marketing their goods or services to you unless you
                    first agree to such disclosure. If you have any questions
                    regarding this policy, or would like to change your
                    preferences, you may contact us.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Do Not Track: Like many websites, our website is not
                    designed to respond to “Do Not Track” signals received from
                    browsers. To learn more about “Do Not Track” signals, you
                    can visit http://www.allaboutdnt.com/.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    California Consumer Privacy Act (CCPA): This part serves as
                    a privacy notice for California residents and applies solely
                    to residents of California. Any terms defined in the CCPA
                    have the same meaning when used in this part. Note that
                    provision of this CCPA notice is not an admission on our
                    part that we are a “business” within the meaning of the
                    CCPA, and nothing in this policy may be construed as such an
                    admission.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Personal Information We Collect </strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We collect certain “Personal Information” as defined by the
                    CCPA and as listed above and have done so within the last 12
                    months.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Personal Information does not include: (a) publicly
                    available information from government records; or (b)
                    de-identified information or aggregate consumer information.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We obtain the categories of Personal Information listed
                    above from the following categories of sources:
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li> - Directly from you</li>
                      <li>
                        {' '}
                        - Indirectly when you use our Services (e.g., cookies
                        when using our website)
                      </li>
                      <li> - From any other sources listed in this policy.</li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Our Use of Personal Information</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may use and have disclosed the Personal Information we
                    collect for the purposes detailed in this Privacy Policy. We
                    will not collect additional categories of Personal
                    Information or use the Personal Information we collected for
                    materially different, unrelated, or incompatible purposes as
                    those set out in these sections without providing you
                    notice.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We do not sell Personal Information as the term “sell” is
                    defined in the CCPA.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Sharing Personal Information</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may disclose any or all of the categories above of your
                    Personal Information to third parties for a business
                    purpose, as set forth in this policy, and we have done so in
                    the last 12 months.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We disclose your Personal Information for a business purpose
                    to the categories of third parties set out in this Privacy
                    Policy.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Your Rights and Choices</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    You have the right to request that we disclose certain
                    information to you about our collection and use of your
                    Personal Information over the past 12 months. Once we
                    receive and confirm your verifiable consumer request (as
                    described below), we will disclose to you (unless an
                    exception applies):
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        - the categories of Personal Information we collected
                        about you;
                      </li>
                      <li>
                        {' '}
                        - the categories of sources for the Personal Information
                        we collected about you;
                      </li>
                      <li>
                        {' '}
                        - our business or commercial purpose for collecting that
                        Personal Information;
                      </li>
                      <li>
                        {' '}
                        - the categories of third parties with whom we share
                        that Personal Information;
                      </li>
                      <li>
                        {' '}
                        - the specific pieces of Personal Information we
                        collected about you (also called a Personal Information
                        portability request);
                      </li>
                      <li>
                        {' '}
                        - if we disclosed your Personal Information for a
                        business purpose, a list of disclosures for a business
                        purpose, identifying the Personal Information categories
                        that each category of recipient obtained;
                      </li>
                    </ul>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We do not provide these Personal Information access and
                    portability rights for B2B Personal Information to the
                    extent such an exception remains in effect under the CCPA.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Deletion Request Rights</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    California residents have the right under the CCPA to
                    request that we delete any of their Personal Information
                    that we have collected and retained, subject to certain
                    exceptions. Once we receive and confirm a verifiable
                    consumer request (see below), we will delete (and direct our
                    service providers to delete) relevant Personal Information
                    from our records, unless an exception applies (such as the
                    B2B exception referenced above).
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We may deny California residents’ deletion request if doing
                    so is permitted or required by applicable law, or if
                    retaining the Personal Information is necessary for us or
                    our service provider(s) to:
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li>
                        {' '}
                        - complete the transaction for which we collected the
                        Personal Information, provide a good or service that you
                        requested, take actions reasonably anticipated within
                        the context of our ongoing business relationship with
                        the requesting individual, or otherwise perform our
                        contract with a requesting individual;
                      </li>
                      <li>
                        {' '}
                        - detect security incidents, protect against malicious,
                        deceptive, fraudulent, or illegal activity, or prosecute
                        those responsible for such activities;
                      </li>
                      <li>
                        {' '}
                        - debug products to identify and repair errors that
                        impair existing intended functionality;
                      </li>
                      <li>
                        {' '}
                        - exercise free speech, ensure the right of another to
                        exercise their free speech rights, or exercise another
                        right provided for by law;
                      </li>
                      <li>
                        {' '}
                        - engage in public or peer-reviewed scientific,
                        historical, or statistical research in the public
                        interest that adheres to all other applicable ethics and
                        privacy laws, when the Personal Information's deletion
                        may likely render impossible or seriously impair the
                        research's achievement, if you previously provided
                        informed consent;
                      </li>
                      <li>
                        {' '}
                        - enable solely internal uses that are reasonably
                        aligned with consumer expectations;
                      </li>
                      <li> - comply with a legal obligation; and</li>
                      <li>
                        {' '}
                        - make other internal and lawful uses of that Personal
                        Information that are compatible with the context in
                        which you provided it.
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Verifiable Consumer Request</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    To exercise the access, portability, and deletion rights
                    under the CCPA described above, please submit to us a
                    verifiable consumer request.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Only a California resident may make a verifiable consumer
                    request related to their Personal Information. A California
                    resident may also make a verifiable consumer request on
                    behalf of their minor child.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    A verifiable consumer request for Personal Information
                    access or portability can only be made twice within a
                    12-month period. The verifiable consumer request must
                    provide sufficient information that allows us to reasonably
                    verify the California resident about whom we collected
                    Personal Information or an authorized representative, and
                    contain sufficient detail that allows us to properly
                    understand, evaluate, and respond to it. We cannot respond
                    to your request or provide Personal Information if we cannot
                    verify your identity or authority to make the request and
                    confirm the Personal Information relates to you.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Making a verifiable consumer request does not require you to
                    create an account on our website. However, we do consider
                    requests made through your password protected account
                    sufficiently verified when the request relates to Personal
                    Information associated with that specific account. We will
                    only use Personal Information provided in a verifiable
                    consumer request to verify the requestor's identity or
                    authority to make the request.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We will endeavor to respond to a verifiable consumer request
                    within 45 days of its receipt. If we require more time (up
                    to 90 days), we will inform you of the reason and extension
                    period in writing. If you have an account with us, we will
                    deliver our written response to that account. If you do not
                    have an account with us, we will deliver our written
                    response by mail or electronically.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Any disclosures we provide will only cover the 12-month
                    period preceding the verifiable consumer request’s receipt.
                    The response we provide will also explain the reasons we
                    cannot comply with a request, if applicable. For Personal
                    Information portability requests, we will select a format to
                    provide your Personal Information that is readily useable
                    and should allow you to transmit the Personal Information
                    from one entity to another entity without hindrance.
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We do not charge a fee to process or respond to your
                    verifiable consumer request unless it is excessive,
                    repetitive, or manifestly unfounded. If we determine that
                    the request warrants a fee, we will tell you why we made
                    that decision and provide you with a cost estimate before
                    completing your request.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Non-Discrimination</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    We will not discriminate against you for exercising any of
                    your CCPA rights. Except as permitted by the CCPA, we will
                    not:
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    <ul role='list'>
                      <li> - Deny you goods or services;</li>
                      <li>
                        {' '}
                        - the categories of sources for the Personal Information
                        we collected about you;
                      </li>
                      <li>
                        {' '}
                        - Charge you different prices or rates for goods or
                        services, including through granting discounts or other
                        benefits or imposing penalties;
                      </li>
                      <li>
                        {' '}
                        - Provide you a different level or quality of goods or
                        services; and
                      </li>
                      <li>
                        {' '}
                        - Suggest that you may receive a different price or rate
                        for goods or services or a different level or quality of
                        goods or services.
                      </li>
                    </ul>
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Links to other sites</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    The Website may contain links to other sites. Supercharge
                    does not control the information collection of sites that
                    can be reached through links from the website. We encourage
                    our users to be aware when they are leaving our site and to
                    read the privacy statements of each website that collects
                    personal information.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>Changes to our Privacy Policy</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    Supercharge is always improving, and as our service evolves
                    we may occasionally update this Privacy Policy. If we modify
                    this privacy policy, we will post the revised privacy policy
                    to the website, and we will also revise the "last updated
                    date" stated above. If we make material changes in the way
                    we use personal information, we will notify you by posting
                    an announcement on our service or by sending you an e-mail.
                    It is your responsibility to periodically review this
                    privacy policy and users are bound by any changes to the
                    privacy policy by using the service after such changes have
                    been first posted.
                  </div>
                  <div className='text-lg text-center max-w-prose mx-auto'>
                    <p className='mt-8 text-xl text-gray-500'>
                      <strong>How to Contact Us</strong>
                    </p>
                  </div>
                  <div className='mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto'>
                    If you have questions or concerns regarding this policy, or
                    any feedback that you would like us to consider, please
                    email us at hello@supercharge.finance.
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </header>
      </div>

      <style global jsx>{`
        body > div:first-child,
        div#__next,
        div#__next > div {
          height: 100%;
        }
      `}</style>
    </div>
  )
}

export default Index
